import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import RefreshIcon from '@material-ui/icons/Refresh'

import Widget from '../../components/Widget'
import {
    createAppointmentType,
    deleteAppointmentType,
    updateAppointmentType,
    useManagementAppointmentTypes,
} from '../../apis'
import { useApiHttpState } from '../../context/ApiContext'
import {
    createIconActionName,
    DestructiveConfirmationDialog,
    FieldValidationError,
    fieldErrorAttributes,
    SectionFeedback,
    useSnackbarSectionInteraction,
} from './primitives'

const SECTION_ID = 'appointment-types'

export const orderAppointmentTypes = records => [...records].sort((left, right) => {
    const insensitive = left.name.toLocaleLowerCase().localeCompare(right.name.toLocaleLowerCase())
    return insensitive || left.name.localeCompare(right.name) || Number(left.id) - Number(right.id)
})

export const validateAppointmentTypeName = (value, records = [], editedId = null) => {
    const name = String(value === undefined || value === null ? '' : value).trim()
    if (!name) return 'Enter an appointment type name.'
    if (name.length > 50) return 'Appointment type names must contain no more than 50 characters.'
    const duplicate = records.some(record =>
        Number(record.id) !== Number(editedId) && String(record.name).trim().toLocaleLowerCase() === name.toLocaleLowerCase())
    return duplicate ? 'An appointment type with this name already exists. Choose a different name.' : ''
}
const conflictGuidance = error => {
    if (!error) return ''
    if (error.code === 'DUPLICATE_TYPE') return 'An appointment type with this name already exists. Choose a different name.'
    return ''
}

export default function AppointmentTypesSection() {
    const source = useManagementAppointmentTypes()
    const { api } = useApiHttpState()
    const interaction = useSnackbarSectionInteraction(source.managementSection || null)
    const { state } = interaction
    const observed = useRef(source.managementSection || null)
    const observedError = useRef(null)
    const headingRef = useRef(null)
    const deleteOpenerRef = useRef(null)
    const [name, setName] = useState('')
    const [editing, setEditing] = useState(null)
    const [editorOpen, setEditorOpen] = useState(false)
    const [clientError, setClientError] = useState('')
    const [deleting, setDeleting] = useState(null)

    useEffect(() => {
        if (source.managementSection && source.managementSection !== observed.current) {
            observed.current = source.managementSection
            interaction.load(() => source.managementSection)
        } else if (source.isError && source.isError !== observedError.current) {
            observedError.current = source.isError
            interaction.load(() => Promise.reject(source.isError))
        }
    }, [interaction, source.isError, source.managementSection])

    const revalidate = useCallback(() => source.revalidate(), [source.revalidate])
    const records = state.confirmedData ? orderAppointmentTypes(state.confirmedData.records) : []
    const versionToken = state.confirmedData && state.confirmedData.versionToken
    const fieldError = clientError || state.fieldErrors.name || conflictGuidance(state.mutationError)
    const controlsDisabled = !interaction.canSubmit

    const resetEditor = () => {
        setEditing(null)
        setEditorOpen(false)
        setName('')
        setClientError('')
        interaction.setDraft(null)
    }

    const beginCreate = () => {
        interaction.clearFeedback()
        setEditing(null)
        setEditorOpen(true)
        setName('')
        setClientError('')
        interaction.setDraft({ mode: 'create', id: null, name: '' })
    }

    const beginEdit = record => {
        interaction.clearFeedback()
        setEditing(record)
        setEditorOpen(true)
        setName(record.name)
        setClientError('')
        interaction.setDraft({ mode: 'edit', id: record.id, name: record.name })
    }

    const submit = async event => {
        event.preventDefault()
        const validationError = validateAppointmentTypeName(name, records, editing && editing.id)
        setClientError(validationError)
        if (validationError) return
        const normalizedName = name.trim()
        const draft = { mode: editing ? 'edit' : 'create', id: editing && editing.id, name }
        interaction.setDraft(draft)
        const result = await interaction.submit({
            mutation: () => editing
                ? updateAppointmentType(api, editing.id, { name: normalizedName }, versionToken)
                : createAppointmentType(api, { name: normalizedName }, versionToken),
            refresh: revalidate,
            draft,
            mutationKey: editing ? `edit:${editing.id}` : 'create',
            successMessage: editing ? `Appointment type renamed to ${normalizedName}.` : `Appointment type ${normalizedName} created.`,
        })
        if (result.ok) resetEditor()
    }

    const requestDelete = (record, event) => {
        interaction.clearFeedback()
        deleteOpenerRef.current = event.currentTarget
        setDeleting(record)
    }
    const confirmDelete = () => interaction.submit({
        mutation: () => deleteAppointmentType(api, deleting.id, versionToken),
        refresh: revalidate,
        mutationKey: `delete:${deleting.id}`,
        successMessage: `Appointment type ${deleting.name} deleted.`,
    })

    return (
        <section aria-labelledby={`${SECTION_ID}-heading`}>
            <Widget
                disableWidgetMenu
                header={
                    <React.Fragment>
                        <Typography
                            ref={headingRef}
                            id={`${SECTION_ID}-heading`}
                            component="h2"
                            variant="h5"
                            color="textSecondary"
                            tabIndex={-1}
                        >
                            Appointment Types
                        </Typography>
                        <div>
                            <IconButton
                                type="button"
                                aria-label="Refresh Appointment Types"
                                onClick={() => interaction.refresh(revalidate)}
                                disabled={controlsDisabled}
                            >
                                <RefreshIcon />
                            </IconButton>
                            <Button
                                type="button"
                                color="primary"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={beginCreate}
                                disabled={controlsDisabled}
                            >
                                Add appointment type
                            </Button>
                        </div>
                    </React.Fragment>
                }
            >
                <SectionFeedback
                    state={state}
                    sectionName="appointment types"
                    onRetryLoad={() => interaction.load(revalidate)}
                    onRetryRefresh={() => interaction.refresh(revalidate)}
                    onAcceptStale={interaction.acceptStale}
                />
                {state.confirmedData && (
                    <React.Fragment>
                        {records.length === 0 ? (
                            <p>No appointment types are configured.</p>
                        ) : (
                            <Table size="small" aria-label="Appointment types">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {records.map(record => (
                                        <TableRow key={record.id}>
                                            <TableCell>{record.name}</TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    type="button"
                                                    aria-label={createIconActionName('Edit appointment type', record.name)}
                                                    onClick={() => beginEdit(record)}
                                                    disabled={controlsDisabled}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    type="button"
                                                    aria-label={createIconActionName('Delete appointment type', record.name)}
                                                    onClick={event => requestDelete(record, event)}
                                                    disabled={controlsDisabled}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <Dialog
                            open={editorOpen}
                            onClose={controlsDisabled ? undefined : resetEditor}
                            aria-labelledby={`${SECTION_ID}-editor-title`}
                            fullWidth
                            maxWidth="sm"
                        >
                            <DialogTitle id={`${SECTION_ID}-editor-title`}>
                                {editing ? `Edit ${editing.name}` : 'Add appointment type'}
                            </DialogTitle>
                            <form onSubmit={submit} noValidate aria-label={editing ? `Edit appointment type ${editing.name}` : 'Create appointment type'}>
                                <DialogContent>
                                    <TextField
                                        autoFocus
                                        id={`${SECTION_ID}-name`}
                                        label="Appointment type name"
                                        value={name}
                                        onChange={event => {
                                            setName(event.target.value)
                                            setClientError('')
                                            interaction.setDraft({ mode: editing ? 'edit' : 'create', id: editing && editing.id, name: event.target.value })
                                        }}
                                        inputProps={{ maxLength: 51, ...fieldErrorAttributes({ sectionId: SECTION_ID, fieldName: 'name', error: fieldError }) }}
                                        error={Boolean(fieldError)}
                                        disabled={controlsDisabled}
                                        required
                                        fullWidth
                                    />
                                    <FieldValidationError sectionId={SECTION_ID} fieldName="name" error={fieldError} />
                                </DialogContent>
                                <DialogActions>
                                    <Button type="button" onClick={resetEditor} disabled={controlsDisabled}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" color="primary" variant="contained" disabled={controlsDisabled}>
                                        {editing ? 'Save appointment type' : 'Add appointment type'}
                                    </Button>
                                </DialogActions>
                            </form>
                        </Dialog>
                    </React.Fragment>
                )}
            </Widget>
            <DestructiveConfirmationDialog
                open={Boolean(deleting)}
                title="Delete appointment type?"
                recordLabel={deleting ? deleting.name : ''}
                consequence="This permanently removes the type from future appointment forms. A type used by any existing appointment cannot be deleted."
                confirmLabel="Delete appointment type"
                onConfirm={confirmDelete}
                onClose={() => setDeleting(null)}
                onConfirmed={() => setDeleting(null)}
                openerRef={deleteOpenerRef}
                sectionHeadingRef={headingRef}
            />
        </section>
    )
}
